// Wealth settings: the companies, dividend tax, living costs, valuation multiple and policy bands (CEO Tasks kept these in Settings → Wealth)
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { patch } from "../../lib/api";
import { Modal } from "../ui";
import { errText, WK, type Company, type WealthDoc, type WealthSettings } from "./shared";

const BANDS: Array<[string, string]> = [["property", "Property"], ["companies", "Companies"], ["investment", "Investments"], ["cash", "Cash"], ["pension", "Pension"]];
// Fields are edited as text and sent as numbers 800 ms after the last keystroke
type Draft = Omit<WealthSettings, "dividendTaxPct" | "livingCostsYear" | "valuationMultiple" | "bands"> & {
  dividendTaxPct: number | string; livingCostsYear?: number | string; valuationMultiple?: number | string; bands?: Record<string, [number | string, number | string]>;
};
const small = "input !min-h-0 !py-1";

export function WealthSettingsModal({ settings, onClose }: { settings: WealthSettings; onClose: () => void }) {
  const qc = useQueryClient();
  const [w, setW] = useState<Draft>(settings);
  const [state, setState] = useState("");
  const [newCo, setNewCo] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const send = async (body: Partial<WealthSettings>) => {
    setState("saving");
    try {
      const r = await patch<{ ok: boolean; settings: WealthSettings }>("/api/wealth/settings", body);
      // Keep what is being typed; only pick up the ids the server gave new companies
      setW((cur) => ({ ...cur, companies: (cur.companies || []).map((c, i) => (c.id ? c : { ...c, id: r.settings.companies?.[i]?.id })) }));
      qc.setQueryData<WealthDoc>(WK.doc, (d) => (d ? { ...d, settings: r.settings } : d));
      void qc.invalidateQueries({ queryKey: WK.strategy });
      void qc.invalidateQueries({ queryKey: WK.review });
      setState("saved");
      setTimeout(() => setState((x) => (x === "saved" ? "" : x)), 1500);
    } catch (e) {
      setState(`error: ${errText(e)}`);
    }
  };
  // Immediate save (company list) vs debounced (number fields)
  const saveNow = (p: Partial<Draft>) => { setW((cur) => ({ ...cur, ...p })); void send(p as Partial<WealthSettings>); };
  const saveLater = (p: Partial<Draft>, body: Partial<WealthSettings>) => {
    setW((cur) => ({ ...cur, ...p }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void send(body), 800);
  };
  const companies = w.companies || [];
  const setCompanies = (list: Company[]) => saveNow({ companies: list });
  const addCompany = () => {
    const name = newCo.trim();
    if (!name) return;
    setNewCo("");
    setCompanies([...companies, { name, accounts: "", notes: "" }]);
  };

  return (
    <Modal title="Wealth settings" onClose={onClose} wide footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
      <p className="muted text-sm">
        Your companies are 100% yours, so their money counts as yours. List them here so cash can be tagged by where it sits, and set the tax you pay on the yearly dividend so the December figure shows net.
      </p>
      <div className="space-y-2">
        {companies.map((c, i) => (
          <div key={c.id || i} className="flex flex-wrap items-center gap-2">
            <input className={`${small} min-w-[140px] flex-1`} type="text" value={c.name} placeholder="Company name" aria-label="Company name" onChange={(e) => setCompanies(companies.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <input className={`${small} min-w-[180px] flex-[2]`} type="text" value={c.accounts} placeholder="Bank accounts (e.g. BOV current, HSBC Cyprus)" aria-label="Bank accounts" onChange={(e) => setCompanies(companies.map((x, j) => (j === i ? { ...x, accounts: e.target.value } : x)))} />
            <button className="btn btn-ghost btn-danger btn-sm !px-1.5" title="Remove company" aria-label="Remove company" onClick={() => { if (window.confirm(`Remove ${c.name || "this company"} from the list? Assets tagged with it keep their tag.`)) setCompanies(companies.filter((_, j) => j !== i)); }}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input className={`${small} flex-1`} type="text" value={newCo} placeholder="Add a company…" aria-label="New company" onChange={(e) => setNewCo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addCompany(); }} />
          <button className="btn btn-sm" onClick={addCompany} disabled={!newCo.trim()}><Plus size={14} /> Add</button>
        </div>
      </div>

      {companies.length > 0 && (
        <div>
          <div className="mb-1.5 text-[0.8125rem] font-semibold text-[var(--text-2)]">Your own value per company <span className="muted font-normal">(optional — leave empty to use the profit multiple below)</span></div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {companies.map((c, i) => (
              <label key={c.id || i} className="muted inline-flex items-center gap-1.5 text-xs">
                {c.name || "Company"}
                <input
                  className={`${small} !w-28`} type="number" min="0" step="10000" value={c.ownValue ?? ""} placeholder="€" aria-label={`Own value of ${c.name}`}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = companies.map((x, j) => (j === i ? { ...x, ownValue: v } : x));
                    saveLater({ companies: next }, { companies: next.map((x, j) => (j === i ? { ...x, ownValue: v === "" ? null : Number(v) } : x)) });
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="divide-line">
        <div className="flex items-center justify-between gap-3 py-2 text-sm">
          <span>Tax on the yearly dividend</span>
          <span className="flex items-center gap-1"><input className={`${small} !w-20 text-right`} type="number" min="0" max="100" step="0.5" value={w.dividendTaxPct} aria-label="Dividend tax percent" onChange={(e) => saveLater({ dividendTaxPct: e.target.value }, { dividendTaxPct: Number(e.target.value) || 0 })} /> %</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-2 text-sm">
          <span>Living costs for the family, per year</span>
          <span className="flex items-center gap-1">€ <input className={`${small} !w-28 text-right`} type="number" min="0" step="5000" value={w.livingCostsYear ?? 0} aria-label="Living costs per year" onChange={(e) => saveLater({ livingCostsYear: e.target.value }, { livingCostsYear: Number(e.target.value) || 0 })} /></span>
        </div>
        <div className="flex items-center justify-between gap-3 py-2 text-sm">
          <span>Company valuation: multiple of average profit <span className="muted">(a modelling assumption, not a valuation)</span></span>
          <span className="flex items-center gap-1"><input className={`${small} !w-20 text-right`} type="number" min="0.5" max="15" step="0.5" value={w.valuationMultiple ?? 4} aria-label="Valuation multiple" onChange={(e) => saveLater({ valuationMultiple: e.target.value }, { valuationMultiple: Number(e.target.value) || 4 })} /> ×</span>
        </div>
        <div className="py-2 text-sm">
          <div className="mb-1.5">Policy bands, % of net worth including the companies <span className="muted">(placeholders — make them yours)</span></div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {BANDS.map(([k, l]) => {
              const b = (w.bands || {})[k] || [0, 0];
              const set = (i: 0 | 1, v: string) => {
                const nb = { ...(w.bands || {}), [k]: (i === 0 ? [v, b[1]] : [b[0], v]) as [number | string, number | string] };
                const nums = Object.fromEntries(Object.entries(nb).map(([kk, [lo, hi]]) => [kk, [Number(lo) || 0, Number(hi) || 0]])) as Record<string, [number, number]>;
                saveLater({ bands: nb }, { bands: nums });
              };
              return (
                <label key={k} className="muted inline-flex items-center gap-1 text-xs">
                  {l}
                  <input className={`${small} !w-14 !px-1.5 text-right`} type="number" min="0" max="100" value={b[0]} aria-label={`${l} band minimum`} onChange={(e) => set(0, e.target.value)} />–
                  <input className={`${small} !w-14 !px-1.5 text-right`} type="number" min="0" max="100" value={b[1]} aria-label={`${l} band maximum`} onChange={(e) => set(1, e.target.value)} />%
                </label>
              );
            })}
          </div>
        </div>
      </div>
      {state && <p className={state.startsWith("error") ? "text-sm text-red-700" : "muted text-sm"}>{state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state}</p>}
    </Modal>
  );
}
